import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import logoZuno from "../assets/zuno-logo.png";
import { isExplicitPassengerName, isPassengerProfileCompleteForUser, resolvePassengerRegistrationDefaults, savePassengerProfile } from "../services/passengerProfileService";

export default function PassengerRegister() {
  const navigate = useNavigate();
  const [nome, setNome] = useState(() => auth.currentUser?.displayName || "");
  const [email, setEmail] = useState(() => auth.currentUser?.emailVerified ? auth.currentUser.email || "" : "");
  const [dataNascimento, setDataNascimento] = useState("");
  const [fotoPreview, setFotoPreview] = useState(() => auth.currentUser?.photoURL || "");
  const [emailConfirmado, setEmailConfirmado] = useState(() => Boolean(auth.currentUser?.email && auth.currentUser?.emailVerified));
  const [perfilExistente, setPerfilExistente] = useState({});
  const [carregandoPerfil, setCarregandoPerfil] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1); // 1 = foto+nome, 2 = email+data

  useEffect(() => {
    let active = true;
    const load = async () => {
      const user = auth.currentUser;
      if (!user) { navigate("/login", { replace:true }); return; }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (!active || auth.currentUser?.uid !== user.uid) return;
        const profile = snap.exists() ? snap.data() : {};
        if (snap.exists() && isPassengerProfileCompleteForUser(user, profile)) {
          navigate("/passenger-home", { replace:true });
          return;
        }
        const defaults = resolvePassengerRegistrationDefaults(user, profile);
        setPerfilExistente(profile);
        setNome(defaults.name);
        setEmail(defaults.email);
        setEmailConfirmado(defaults.emailVerifiedByProvider);
        setDataNascimento(defaults.birthDate);
        setFotoPreview(defaults.photoURL);
        setStep(isExplicitPassengerName(defaults.name) ? 2 : 1);
      } catch {
        if (active) {
          const defaults = resolvePassengerRegistrationDefaults(user);
          setNome(defaults.name); setEmail(defaults.email);
          setEmailConfirmado(defaults.emailVerifiedByProvider); setFotoPreview(defaults.photoURL);
          setStep(isExplicitPassengerName(defaults.name) ? 2 : 1);
        }
      } finally {
        if (active) setCarregandoPerfil(false);
      }
    };
    load();
    return () => { active = false; };
  }, [navigate]);

  const handleNextStep = () => {
    if (!nome.trim()) {
      setError("Por favor, insira seu nome completo.");
      return;
    }
    if (nome.trim().split(" ").length < 2) {
      setError("Por favor, insira seu nome e sobrenome.");
      return;
    }
    setError("");
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setError("Por favor, insira um e-mail válido.");
      return;
    }
    if (!dataNascimento) {
      setError("Por favor, insira sua data de nascimento.");
      return;
    }

    // Verificar idade mínima (18 anos)
    const nascimento = new Date(dataNascimento);
    const hoje = new Date();
    const idade = hoje.getFullYear() - nascimento.getFullYear();
    if (idade < 18) {
      setError("Você precisa ter pelo menos 18 anos.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login", { replace: true });
        return;
      }

      // O nome vem somente do campo preenchido pelo usuário. O UID e telefone
      // vêm exclusivamente da sessão Firebase confirmada, nunca do navegador.
      await savePassengerProfile({
        user, name: nome, email, birthDate: dataNascimento, photoURL: fotoPreview,
        existingProfile: perfilExistente,
        database: db, getCurrentUid: () => auth.currentUser?.uid,
      });

      // Atualiza localStorage
      localStorage.setItem("usuarioLogado", nome.trim());
      localStorage.setItem("usuarioFoto", fotoPreview);
      localStorage.setItem("userEmail", email.trim());
      localStorage.setItem("userType", "passenger");

      navigate("/passenger-home", { replace: true });
    } catch (err) {
      console.error("Erro ao salvar perfil:", err);
      setError("Erro ao salvar perfil. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const primeiroNome = nome.trim().split(" ")[0] || "você";
  const nomeConfirmado = isExplicitPassengerName(nome);
  const nascimentoConfirmado = Boolean(perfilExistente.dataNascimento);

  if (carregandoPerfil) return <div className="zuno-app-shell passenger-register-screen" style={s.root} />;

  return (
    <div className="zuno-app-shell passenger-register-screen" style={s.root}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes scaleIn { from{opacity:0;transform:scale(0.92)} to{opacity:1;transform:scale(1)} }
        @keyframes orb1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(20px,-15px)} }
        @keyframes orb2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-15px,20px)} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.04)} }
        @keyframes shimmer {
          0%{background-position:-200% 0}
          100%{background-position:200% 0}
        }
        * { box-sizing: border-box; }
        input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 100px rgba(13,15,40,0.95) inset !important;
          -webkit-text-fill-color: #fff !important;
        }
        .zp-input { transition: border-color 0.2s, box-shadow 0.2s; }
        .zp-input:focus {
          outline: none;
          border-color: rgba(168,85,247,0.7) !important;
          box-shadow: 0 0 0 3px rgba(168,85,247,0.15) !important;
        }
        .zp-input::placeholder { color: rgba(255,255,255,0.22); }
        .foto-btn:hover { opacity: 0.85; }
        .zp-btn:active { transform: scale(0.98); }
      `}</style>

      {/* Background */}
      <div style={s.bg}>
        <div style={{ ...s.orb, width: 480, height: 480, background: "radial-gradient(circle,rgba(124,58,237,0.18) 0%,transparent 70%)", top: -150, left: -150, animation: "orb1 9s ease-in-out infinite" }} />
        <div style={{ ...s.orb, width: 380, height: 380, background: "radial-gradient(circle,rgba(6,182,212,0.12) 0%,transparent 70%)", bottom: -100, right: -100, animation: "orb2 12s ease-in-out infinite" }} />
      </div>

      <div style={s.container}>

        {/* Logo */}
        <div style={{ animation: "fadeUp 0.4s ease both", textAlign: "center", marginBottom: 24 }}>
          <img src={logoZuno} alt="Zuno" style={{ height: 44, objectFit: "contain" }} />
        </div>

        {/* Card principal */}
        <div style={{ animation: "scaleIn 0.4s ease 0.1s both", ...s.card }}>

          {/* Progress steps */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 28 }}>
            {[1, 2].map((n) => (
              <React.Fragment key={n}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: step >= n
                    ? "linear-gradient(135deg,#7c3aed,#a855f7)"
                    : "rgba(255,255,255,0.07)",
                  border: step >= n ? "none" : "1.5px solid rgba(255,255,255,0.12)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, fontWeight: 700,
                  color: step >= n ? "#fff" : "rgba(255,255,255,0.3)",
                  transition: "all 0.3s", flexShrink: 0,
                }}>
                  {step > n ? (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : n}
                </div>
                {n < 2 && (
                  <div style={{
                    flex: 1, height: 2, borderRadius: 2,
                    background: step > n
                      ? "linear-gradient(90deg,#7c3aed,#a855f7)"
                      : "rgba(255,255,255,0.07)",
                    transition: "all 0.3s",
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* STEP 1 — Foto + Nome */}
          {step === 1 && (
            <div style={{ animation: "fadeUp 0.35s ease both" }}>
              <h2 style={s.title}>Vamos criar seu perfil</h2>
              <p style={s.subtitle}>Só precisamos de algumas informações rápidas para te identificar nas corridas.</p>

              {fotoPreview && (
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", marginBottom:24 }}>
                  <img src={fotoPreview} alt="Foto da conta" style={{ width:80, height:80, borderRadius:"50%", objectFit:"cover", border:"3px solid #a855f7" }} />
                  <span style={{ ...s.label, marginTop:10, marginBottom:0 }}>Foto da sua conta</span>
                </div>
              )}

              {/* Nome */}
              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>Nome completo</label>
                {nomeConfirmado ? (
                  <div className="zp-input" style={{ ...s.input, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span>{nome}</span><span style={{ color:"#22c55e", fontSize:12 }}>Da sua conta</span>
                  </div>
                ) : (
                  <input className="zp-input" type="text" placeholder="Ex: João Silva" value={nome}
                    onChange={e => { setNome(e.target.value); setError(""); }} autoComplete="name" style={s.input} />
                )}
              </div>

              {error && <ErrorBox msg={error} />}

              <button
                className="zp-btn"
                onClick={handleNextStep}
                style={s.btnMain}
              >
                Continuar
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </button>
            </div>
          )}

          {/* STEP 2 — Email + Data */}
          {step === 2 && (
            <div style={{ animation: "fadeUp 0.35s ease both" }}>
              <h2 style={s.title}>Quase lá, {primeiroNome}! 👋</h2>
              <p style={s.subtitle}>Preencha somente o que ainda falta para concluir seu perfil.</p>

              {/* E-mail */}
              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>E-mail</label>
                {emailConfirmado ? (
                  <div className="zp-input" style={{ ...s.input, display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
                    <span style={{ overflow:"hidden", textOverflow:"ellipsis" }}>{email}</span>
                    <span style={{ color:"#22c55e", fontSize:12, whiteSpace:"nowrap" }}>Verificado</span>
                  </div>
                ) : (
                  <input className="zp-input" type="email" placeholder="seu@email.com" value={email}
                    onChange={e => { setEmail(e.target.value); setError(""); }} autoComplete="email" inputMode="email" style={s.input} />
                )}
              </div>

              {/* Data de nascimento */}
              <div style={{ marginBottom: 20 }}>
                <label style={s.label}>Data de nascimento</label>
                {nascimentoConfirmado ? (
                  <div className="zp-input" style={{ ...s.input, display:"flex", justifyContent:"space-between" }}>
                    <span>{dataNascimento.split("-").reverse().join("/")}</span>
                    <span style={{ color:"#22c55e", fontSize:12 }}>Já informada</span>
                  </div>
                ) : (
                  <>
                    <input className="zp-input" type="date" value={dataNascimento}
                      onChange={e => { setDataNascimento(e.target.value); setError(""); }}
                      max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split("T")[0]}
                      style={{ ...s.input, colorScheme:"dark" }} />
                    <p style={{ fontSize:11, color:"rgba(255,255,255,0.2)", margin:"6px 0 0 4px" }}>Idade mínima: 18 anos</p>
                  </>
                )}
              </div>

              {/* Info de segurança */}
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", background: "rgba(6,182,212,0.06)", borderRadius: 12, border: "1px solid rgba(6,182,212,0.12)", marginBottom: 20 }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>🔒</span>
                <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", margin: 0, lineHeight: 1.6 }}>
                  Seus dados são protegidos e compartilhados apenas com os prestadores necessários ao serviço, conforme a Política de Privacidade.
                </p>
              </div>

              {error && <ErrorBox msg={error} />}

              <button
                className="zp-btn"
                onClick={handleSubmit}
                disabled={loading}
                style={{ ...s.btnMain, opacity: loading ? 0.75 : 1, cursor: loading ? "not-allowed" : "pointer" }}
              >
                {loading ? (
                  <>
                    <span style={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
                    Salvando perfil...
                  </>
                ) : (
                  <>
                    Finalizar cadastro
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </>
                )}
              </button>

              <button
                onClick={() => { setStep(1); setError(""); }}
                style={{ width: "100%", marginTop: 12, background: "none", border: "none", color: "rgba(255,255,255,0.25)", fontSize: 13, cursor: "pointer", padding: "8px" }}
              >
                ← Voltar
              </button>
            </div>
          )}

        </div>

        {/* Rodapé */}
        <p style={{ textAlign: "center", marginTop: 20, fontSize: 12, color: "rgba(255,255,255,0.15)", animation: "fadeUp 0.4s ease 0.3s both" }}>
          Suporte:{" "}
          <a href="tel:+5511917934340" style={{ color: "#a855f7", textDecoration: "none", fontWeight: 600 }}>
            (11) 91793-4340
          </a>
        </p>
      </div>
    </div>
  );
}

function ErrorBox({ msg }) {
  return (
    <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 12, padding: "10px 14px", marginBottom: 14 }}>
      <p style={{ color: "#f87171", fontSize: 13, margin: 0 }}>⚠️ {msg}</p>
    </div>
  );
}

const s = {
  root: {
    minHeight: "100vh",
    background: "var(--zuno-app-background)",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    position: "relative", overflow: "hidden",
  },
  bg: { position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" },
  orb: { position: "absolute", borderRadius: "50%" },
  container: {
    position: "relative", zIndex: 1,
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", minHeight: "100vh",
    padding: "32px 16px",
  },
  card: {
    width: "100%", maxWidth: 400,
    background: "var(--zuno-surface-translucent)",
    backdropFilter: "blur(20px)",
    border: "1.5px solid var(--zuno-line)",
    borderRadius: 28,
    padding: "28px 24px 24px",
    boxShadow: "var(--zuno-shadow)",
  },
  title: {
    color: "var(--zuno-text)", fontSize: 22, fontWeight: 800,
    margin: "0 0 8px", lineHeight: 1.3,
  },
  subtitle: {
    color: "var(--zuno-muted)", fontSize: 14,
    margin: "0 0 24px", lineHeight: 1.6,
  },
  label: {
    display: "block", fontSize: 12, fontWeight: 600,
    color: "var(--zuno-muted)", marginBottom: 8,
    letterSpacing: 0.5, textTransform: "uppercase",
  },
  input: {
    width: "100%", padding: "13px 18px",
    borderRadius: 14,
    border: "1.5px solid var(--zuno-line)",
    background: "var(--zuno-elevated)",
    color: "var(--zuno-text)", fontSize: 15,
  },
  btnMain: {
    width: "100%", padding: "15px",
    borderRadius: 50, border: "none",
    background: "linear-gradient(135deg,#7c3aed 0%,#a855f7 50%,#ec4899 100%)",
    color: "#fff", fontSize: 15, fontWeight: 700,
    cursor: "pointer", letterSpacing: 0.3,
    boxShadow: "0 8px 24px rgba(124,58,237,0.35)",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    transition: "transform 0.15s",
  },
};
